/**
 * Inventory Feature - Stock Utility Functions
 * Functions for stock status calculation
 */
import type { Product } from '../types';

/**
 * Determine stock level status based on notify threshold ratio
 * @param currentStock - Current stock quantity
 * @param notifyLevel - Notify threshold from database
 * @returns Object with status and corresponding CSS classes
 */
export const getStockStatus = (currentStock: number, notifyLevel: number | null) => {
  // Fallback to absolute system if no notify level
  if (!notifyLevel || notifyLevel <= 0) {
    if (currentStock <= 0) return { status: 'critical', barColor: 'bg-red-500', bgColor: 'bg-red-100', textColor: 'text-red-800' };
    if (currentStock < 10) return { status: 'low', barColor: 'bg-yellow-500', bgColor: 'bg-yellow-100', textColor: 'text-yellow-800' };
    return { status: 'good', barColor: 'bg-[#A3D95A]', bgColor: 'bg-green-100', textColor: 'text-green-800' };
  }

  // Ratio-based system: ratio = currentStock / notifyLevel
  const ratio = currentStock / notifyLevel;

  if (ratio <= 1) {
    // At or below reorder point - CRITICAL
    return { status: 'critical', barColor: 'bg-red-500', bgColor: 'bg-red-100', textColor: 'text-red-800' };
  } else if (ratio <= 2) {
    // Above reorder but not comfortable - LOW
    return { status: 'low', barColor: 'bg-yellow-500', bgColor: 'bg-yellow-100', textColor: 'text-yellow-800' };
  } else {
    // Well stocked - GOOD
    return { status: 'good', barColor: 'bg-[#A3D95A]', bgColor: 'bg-green-100', textColor: 'text-green-800' };
  }
};

/** Canonical available-stock derivation — Shopify-style available = on-hand − committed − unavailable, preferring the server-computed value when present. */
export function availableStock(p: { available?: number | null; onHand?: number | null; stockLevel: number; committed?: number | null; unavailable?: number | null }): number {
  return p.available ?? ((p.onHand ?? p.stockLevel) - (p.committed ?? 0) - (p.unavailable ?? 0));
}

/**
 * Phase 92 (BLU-1426): the 3PL's shared pool figure (before hold-back) on a
 * client's own supply alias row, else null. The server sends it to no other
 * row. An alias holds no stock of its own, so read this ahead of its zero.
 */
export function supplyPoolFigure(p: Pick<Product, 'supplyAlias' | 'supplyPoolAvailable'>): number | null {
  return p.supplyAlias && typeof p.supplyPoolAvailable === 'number' ? p.supplyPoolAvailable : null;
}

/**
 * BLU-1458: the back-up stock a row can draw on (the 3PL's stock behind the
 * row's own SKU, which the client buys from the 3PL): a figure above 0 with
 * the owner's name, never on a supply alias. Else 0, so a negative figure
 * never takes from the row's own stock. BLU-1268: a just-in-time back-up is
 * unlimited, not a figure (`justInTimeSource` says so), so it adds 0 here.
 */
export function backupFigure(
  p: Pick<Product, 'supplyAlias' | 'fallbackAvailable' | 'fallbackOwnerName' | 'fallbackJustInTime' | 'fallbackKind'>,
): number {
  const figure = p.fallbackAvailable ?? 0;
  // BLU-1527 S3: an own substitute's figure has no owner name (it is the
  // caller's own garment); `fallbackKind: 'own'` stands in for it.
  const hasSource = !!p.fallbackOwnerName || p.fallbackKind === 'own';
  return !p.supplyAlias && !p.fallbackJustInTime && figure > 0 && hasSource ? figure : 0;
}

/**
 * BLU-1268: where a row's Just in time comes from, or null when it has none.
 *   - `own`: the row's own pair is just in time (its figures are the store
 *     constant, not a count);
 *   - `pool`: a client's supply alias on the 3PL's just-in-time blank;
 *   - `backup`: the row's own stock is counted, and the 3PL's blank behind it
 *     is just in time, so the back-up is unlimited.
 * Any of them means the row never runs out: orders never wait for it.
 */
export type JustInTimeSource = 'own' | 'pool' | 'backup';

export function justInTimeSource(
  p: Pick<
    Product,
    'stockMode' | 'supplyAlias' | 'supplyPoolJustInTime' | 'fallbackJustInTime' | 'fallbackOwnerName' | 'fallbackKind'
  >,
): JustInTimeSource | null {
  if (p.stockMode === 'just_in_time') return 'own';
  if (p.supplyAlias) return p.supplyPoolJustInTime ? 'pool' : null;
  return p.fallbackJustInTime && (p.fallbackOwnerName || p.fallbackKind === 'own') ? 'backup' : null;
}

/** BLU-1458: the figure shown with back-up: own plus back-up, clamped at 0. Without back-up, own as is. */
export function withBackup(own: number, backup: number): number {
  return backup > 0 ? Math.max(0, own + backup) : own;
}

/** BLU-1458: back-up alone covers the figure (own at or below 0, the total above 0). */
export function coveredByBackup(own: number, backup: number): boolean {
  return backup > 0 && own <= 0 && withBackup(own, backup) > 0;
}

/** BLU-1458: the orange state of a figure that back-up alone covers. */
export const BACKUP_STOCK_STATUS = {
  status: 'backup',
  barColor: 'bg-orange-500',
  bgColor: 'bg-orange-100 dark:bg-orange-900/30',
  textColor: 'text-orange-800 dark:text-orange-400',
};

/**
 * BLU-1458: the status of a figure that may carry back-up. Back-up is stock
 * the client can sell, so the status follows the total, orange when back-up
 * alone covers it; a total of 0 is critical as before.
 */
export function stockStatusWithBackup(own: number, backup: number, notifyLevel: number | null) {
  return coveredByBackup(own, backup) ? BACKUP_STOCK_STATUS : getStockStatus(withBackup(own, backup), notifyLevel);
}

/** The value the stock sort orders by: an alias row's shared pool, else its stockLevel. */
export function stockSortValue(p: Product): number {
  return supplyPoolFigure(p) ?? p.stockLevel;
}

/**
 * The figure the out and low stock filters read: an alias row's shared pool,
 * else its available stock plus any back-up (BLU-1458), as the List cell
 * shows it, at one location and on All Locations alike.
 */
function stockFilterFigure(p: Product): number {
  return supplyPoolFigure(p) ?? withBackup(availableStock(p), backupFigure(p));
}

/** BLU-1268: a just-in-time row (its own, its pool or its back-up) is never out of stock. */
export function isOutOfStock(p: Product): boolean {
  if (justInTimeSource(p)) return false;
  return stockFilterFigure(p) <= 0;
}

/** Low or critical against the row's notify level; never a just-in-time row (BLU-1268). */
export function isLowStock(p: Product): boolean {
  if (justInTimeSource(p)) return false;
  const status = getStockStatus(stockFilterFigure(p), p.notify ?? null).status;
  return status === 'low' || status === 'critical';
}
